import React, { useRef, useEffect, useMemo } from "react";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip);

const BarChart = ({ onDaySelect, selectedDayIndex = null, impressionsData = null, loading = false }) => {
  const chartRef = useRef(null);
  
  // DEBUG: Log all incoming data
  React.useEffect(() => {
    console.log('🔍 BarChart DEBUG - Raw impressionsData:', impressionsData);
    if (impressionsData?.dailyData) {
      console.log('🔍 BarChart DEBUG - Daily data breakdown:');
      impressionsData.dailyData.forEach((day, index) => {
        console.log(`  Day ${index} (${['Mon','Tue','Wed','Thu','Fri','Sat','Sun'][index]}):`, {
          date: day.date,
          day: day.day,
          count: day.count,
          isToday: day.isToday
        });
      });
    }
  }, [impressionsData]);
  
  // Define dayLabels with useMemo to prevent re-renders
  const dayLabels = useMemo(() => ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'], []);
  
  
  // Get current date and calculate the week (Monday to Sunday) in Pacific Time
  // This should match the backend's Pacific Time calculation
  const getCurrentWeekDays = () => {
    // Create date in Pacific Time to match backend logic
    const today = new Date();
    
    // Convert to Pacific Time (America/Los_Angeles)
    const pacificToday = new Date(today.toLocaleString("en-US", {timeZone: "America/Los_Angeles"}));
    const currentDay = pacificToday.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay; // Calculate offset to Monday
    
    const monday = new Date(pacificToday);
    monday.setDate(pacificToday.getDate() + mondayOffset);
    
    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(monday);
      day.setDate(monday.getDate() + i);
      weekDays.push(day);
    }
    return weekDays;
  };

  const weekDays = getCurrentWeekDays();
  
  // DEBUG: Log Pacific Time calculations
  React.useEffect(() => {
    const today = new Date();
    const pacificToday = new Date(today.toLocaleString("en-US", {timeZone: "America/Los_Angeles"}));
    
    console.log('🔍 BarChart DEBUG - Time calculations:');
    console.log('  Browser time:', today.toISOString());
    console.log('  Pacific time:', pacificToday.toISOString());
    console.log('  Pacific day of week (0=Sun, 5=Fri):', pacificToday.getDay());
    console.log('  Expected day name:', ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][pacificToday.getDay()]);
    console.log('  Week days calculated:', weekDays.map(day => day.toDateString()));
  }, [weekDays]);
  
  // Use impressions data from props, or fallback to empty week
  const weeklyImpressions = React.useMemo(() => {
    if (!impressionsData?.dailyData) {
      // Fallback empty week when no real data is available
      console.log('🔍 BarChart DEBUG - No data, using empty week');
      return [0, 0, 0, 0, 0, 0, 0]; // Mon-Sun empty data
    }
    
    // Map backend data to correct day order (Mon-Sun)
    // Backend sends data in date order (Sat-Fri), but we need Mon-Sun order
    const dayMap = {
      'Mon': 0, 'Tue': 1, 'Wed': 2, 'Thu': 3, 
      'Fri': 4, 'Sat': 5, 'Sun': 6
    };
    
    const counts = [0, 0, 0, 0, 0, 0, 0]; // Initialize Mon-Sun array
    
    impressionsData.dailyData.forEach(day => {
      const dayIndex = dayMap[day.day];
      if (dayIndex !== undefined) {
        counts[dayIndex] = day.count;
      }
    });
    
    console.log('🔍 BarChart DEBUG - Corrected weekly impressions mapping:');
    dayLabels.forEach((label, index) => {
      console.log(`  ${label}: ${counts[index]} impressions`);
    });
    return counts;
  }, [impressionsData, dayLabels]);
  
  // Find the current day to highlight it (using Pacific Time)
  const today = new Date();
  const pacificToday = new Date(today.toLocaleString("en-US", {timeZone: "America/Los_Angeles"}));
  const todayIndex = React.useMemo(() => {
    if (impressionsData?.dailyData) {
      // Use the isToday flag from API data - data is now in Mon-Sun order
      const todayDataIndex = impressionsData.dailyData.findIndex(day => day.isToday);
      if (todayDataIndex !== -1) {
        return todayDataIndex;
      }
    }
    
    // Fallback to calculating from weekDays using Pacific Time
    return weekDays.findIndex(day => 
      day.toDateString() === pacificToday.toDateString()
    );
  }, [impressionsData, weekDays, pacificToday]);
  
  
  // Force Chart.js to update when selectedDayIndex changes
  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.update('none'); // 'none' disables animation for immediate update
    }
  }, [selectedDayIndex]);

  // Simplified click handler with manual position calculation
  const handleBarClick = (event, elements, chart) => {
    // Stop the event from bubbling up to prevent link selection
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }
    if (event && event.preventDefault) {
      event.preventDefault();
    }
    
    
    // If Chart.js detected elements, use that
    if (elements.length > 0 && onDaySelect) {
      const clickedIndex = elements[0].index;
      
      const clickedDay = {
        index: clickedIndex,
        day: dayLabels[clickedIndex],
        date: weekDays[clickedIndex],
        impressions: weeklyImpressions[clickedIndex]
      };
      
      onDaySelect(clickedDay);
    } else {
      // Fallback: Simple position-based calculation
      if (chart && onDaySelect) {
        // Get canvas bounds and click position
        const canvas = chart.canvas;
        const rect = canvas.getBoundingClientRect();
        const clickX = event.clientX - rect.left;
        
        // Calculate chart area (excluding padding)
        const chartArea = chart.chartArea;
        const chartWidth = chartArea.right - chartArea.left;
        const barWidth = chartWidth / dayLabels.length;
        
        // Calculate which bar was clicked
        const relativeX = clickX - chartArea.left;
        const clickedIndex = Math.floor(relativeX / barWidth);
        
        // Make sure index is within bounds
        if (clickedIndex >= 0 && clickedIndex < dayLabels.length) {
          const clickedDay = {
            index: clickedIndex,
            day: dayLabels[clickedIndex],
            date: weekDays[clickedIndex],
            impressions: weeklyImpressions[clickedIndex]
          };
          onDaySelect(clickedDay);
        }
      }
    }
  };

  // Use useMemo to ensure background colors are calculated with current props
  const data = useMemo(() => {
    const backgroundColors = dayLabels.map((_, i) => {
      // Highlight selected day, or today if no selection
      // IMPORTANT: Check for null/undefined explicitly since selectedDayIndex can be 0 (Monday)
      const highlightIndex = (selectedDayIndex !== null && selectedDayIndex !== undefined) ? selectedDayIndex : todayIndex;
      const isHighlighted = i === highlightIndex;
      return isHighlighted ? "#007bff" : "#cce0ff";
    });
    
    return {
      labels: dayLabels,
      datasets: [
        {
          data: weeklyImpressions,
          backgroundColor: backgroundColors,
          borderRadius: 6,
          borderWidth: 0,
          hoverBackgroundColor: dayLabels.map((_, i) => {
            // IMPORTANT: Check for null/undefined explicitly since selectedDayIndex can be 0 (Monday)
            const highlightIndex = (selectedDayIndex !== null && selectedDayIndex !== undefined) ? selectedDayIndex : todayIndex;
            return i === highlightIndex ? "#0056b3" : "#b3d9ff";
          }),
        },
      ],
    };
  }, [selectedDayIndex, todayIndex, dayLabels, weeklyImpressions]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: handleBarClick,
    layout: {
      padding: {
        top: 5,
        bottom: 5,
        left: 5,
        right: 5
      }
    },
    plugins: {
      legend: { display: false },
      tooltip: { 
        enabled: true,
        backgroundColor: '#1a1a1a',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: '#333333',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          title: (tooltipItems) => {
            const index = tooltipItems[0].dataIndex;
            const day = weekDays[index];
            return day.toLocaleDateString('en-US', { 
              weekday: 'long', 
              month: 'short', 
              day: 'numeric' 
            });
          },
          label: (context) => {
            return `${context.parsed.y} impressions`;
          },
          afterLabel: () => {
            return 'Click to select this day';
          }
        }
      },
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: false,
        },
        ticks: {
          color: '#666666',
          font: {
            size: 11,
            family: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            weight: '500'
          },
          maxRotation: 0,
        },
        border: {
          display: false,
        }
      },
      y: {
        display: false,
        beginAtZero: true,
      },
    },
    elements: {
      bar: {
        borderSkipped: false,
      }
    },
    interaction: {
      intersect: false,  // Allow clicks even when not directly over bar
      mode: 'index'     // Use index mode to detect clicks by column
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div 
        style={{ 
          width: '100%', 
          height: '80px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f8f9fa',
          borderRadius: '6px'
        }}
      >
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px',
          color: '#6b7280',
          fontSize: '0.875rem'
        }}>
          <div 
            style={{
              width: '16px',
              height: '16px',
              border: '2px solid #e5e7eb',
              borderTop: '2px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}
          />
          Loading impressions...
        </div>
        <style jsx>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div 
      style={{ width: '100%', height: '80px' }}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      onMouseDown={(e) => {
        e.stopPropagation();
      }}
    >
      <Bar 
        ref={chartRef} 
        data={data} 
        options={options} 
        key={`chart-${selectedDayIndex}-${todayIndex}`}
      />
    </div>
  );
};

export default BarChart;
